import React from 'react';
import {
  Activity,
  AlignJustify,
  CapsulePills,
  CheckList,
  Clipboard,
  File,
  FileText,
  Gauge,
  HeartList,
  Image,
  Lifeology,
  List,
  PlotCurve,
  Type,
  Upload,
  Watch,
  ClockRewind,
  Cpu,
  ActivityFolder,
  Sparkle,
} from '@lifeomic/chromicons';
import SkillSpringIcon from './SkillSpring/icon';
import { Consent, Genomics } from '../../../components/Icons';
import { ModuleTypeConfig } from '../../LayoutConfiguration/types';
import AISystemSummary from './ExecutiveSummary';
import { AiChat, AITools } from './AITools';
import LabResults from './LabResults';

export const intlErrorMessage = {
  id: 'SubjectViewer.module.moduleErrorMessages',
  defaultMessage: 'Error Occurred In Module',
};

const CGMData = React.lazy(() => import('./CGMData'));
const Graph = React.lazy(() => import('./Graph'));
const LifeologyModule = React.lazy(() => import('./LifeologyModule'));
const Medications = React.lazy(() => import('./Medications'));
const Measurements = React.lazy(() => import('./Measurements'));
const MolecularTumorBoardNotes = React.lazy(
  () => import('./MolecularTumorBoardNotes'),
);
const Report = React.lazy(() => import('./Report'));
const SkillSpring = React.lazy(() => import('./SkillSpring'));
const SubjectFileUpload = React.lazy(() => import('./SubjectFileUpload'));
const SubjectPhotos = React.lazy(() => import('./SubjectPhotos'));
const SurveyResults = React.lazy(() => import('./SurveyResults'));
const Table = React.lazy(() => import('./Table'));
const TextModule = React.lazy(() => import('./Text'));
const TrialMatching = React.lazy(() => import('./TrialMatching'));
const WorkbookIngest = React.lazy(() => import('./WorkbookIngest'));
const RiskCalculator = React.lazy(
  () => import('./RiskCalculators/RiskCalculator'),
);
const SynopticHealthView = React.lazy(() => import('./SynopticHealthView'));
const Wearables = React.lazy(() => import('./Wearables'));
const Forms = React.lazy(() => import('./Forms'));
const DICOMViewer = React.lazy(() => import('./DICOMViewer'));
const StandardTimeline = React.lazy(
  () => import('./Timeline/StandardTimeline'),
);
const LLMTimeline = React.lazy(() => import('./Timeline/LLMTimeline'));
const MLLabeler = React.lazy(() => import('./ML/MLLabeler'));
const Summary = React.lazy(() => import('./PatientSummaries'));
const ClinicalGenotypes = React.lazy(
  () => import('./ClinicalGenotypes/ClinicalGenotypes'),
);
const SubjectReports = React.lazy(() => import('./SubjectReports'));
const Notes = React.lazy(() => import('./Notes'));
const ActionPlan = React.lazy(() => import('./ActionPlan'));

// These are static uuids defined for the modules. Generate a new one with `npx uuid | pbcopy`
export enum ModuleType {
  Graph = '960fcc82-17f3-4a8f-91e2-dfd717104725',
  Table = '83387f5f-0e8c-456d-b528-7c1f62bcc5e4',
  Report = '1114bb1d-ced5-4721-9df8-b7b727c30e27',
  SubjectPhotos = '249ea3f9-5f88-4c28-ab70-19a1b61dfb49',
  Medications = '2c11b7c6-92d7-4ea6-b791-a913e887a406',
  Measurements = '1d389a8e-6825-49ff-8eed-d2aca9d2293a',
  Wearables = '3bbfa106-8f05-11ea-bc55-0242ac130003',
  Text = 'fc1305d4-6150-45c6-a34f-bd25fa6fbe94',
  Lifeology = 'ca899765-c2f0-4f86-8195-84ff5b18ae52',
  SkillSpring = '72607cfe-418a-4717-ac9a-506c73b7d350',
  SynopticHealth = 'bfd17bf0-14ff-4795-b5c1-a63e18d6cf18',
  MolecularTumorBoardNotes = '07de7abc-fb68-410c-a21a-6cb1a1893ee3',
  SurveyResults = 'aa0e465b-369b-4627-bc7d-2315e5ae07b4',
  SubjectDataUpload = '9d30573f-794c-447a-8ca0-160af5be30d0a',
  TrialMatching = '3307a533-160a-42dc-aff6-4676c03f2221',
  RiskCalculator = '9f8222c8-bb04-475b-bfa6-410b6d8cb6ac',
  CGMData = '35327848-633b-4e20-ae81-d7daf069aca7',
  SubjectFileUpload = '2405f861-5815-49a3-b4ae-0745bff5835d',
  Forms = '4454bf9a-1505-45bf-9762-c7551783037f',
  DICOMViewer = 'ff77d8a7-ab1f-4264-b1e4-2dd4b6e07d7b',
  Timeline = '2d3a19ec-b76d-4dc6-86f6-bcd4cefe273f',
  MLLabeler = '09483666-8c56-450d-ac8b-7b7a5f4a34e7',
  LLMTimeline = '2db16834-0dcd-4c0e-9584-807c8b78c158',
  Summary = '01fc0d75-05bd-4a9c-9efd-ffc688dde4cc',
  ClinicalGenotypes = '307be8b9-f256-45bd-8e70-ffee2ea0a7ae',
  SubjectReports = 'ecc43823-941e-471c-9a1b-f991a08d70d7',
  Notes = '892ddd82-7d0c-41e5-a653-dc5d20559f3e',
  ActionPlan = 'b5250cd8-e4b5-4e4d-8f4e-1b4a1dea862f',
  AnnualReport = '5c69d41d-6251-4037-bc0a-f349d5fb3ecb',
  LabResults = '4cc783fd-212f-4718-b2a2-c39976b52f2a',
  AiChat = 'b64bad79-5a1d-4ac6-aadc-2bb2663da2d5',
  AiReports = 'd7af2393-f012-40e9-bb34-2a09e86a89b0',
}

export type BaseModuleTypesConfig = {
  [k in ModuleType]: ModuleTypeConfig<any>;
};

export const basicModuleTypesConfig: BaseModuleTypesConfig = {
  // Configurable graph
  [ModuleType.Graph]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Graph',
    moduleTypeDescription: 'A configurable graph to display patient data',
    icon: PlotCurve,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: Graph,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // module size config
    layout: {
      w: 6,
      h: 20,
      minW: 3,
      minH: 10,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // default title to display on the module heading
    title: 'Configurable Graph',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
  },
  [ModuleType.Table]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Table',
    moduleTypeDescription: 'A configurable table to view patient data',
    icon: List,
    // Does this module dynamically set its height
    hasDynamicHeight: true,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: Table,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // module size config
    layout: {
      w: 6,
      h: 20,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // default title to display on the module heading
    title: 'Configurable Table',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
  },
  [ModuleType.Report]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Report',
    moduleTypeDescription: 'View any patient data in report form',
    icon: Consent,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: Report,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // module size config
    layout: {
      w: 6,
      h: 20,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // default title to display on the module heading
    title: 'Report',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: () => true,
  },
  [ModuleType.SubjectPhotos]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Subject Photos',
    moduleTypeDescription: 'View subject photos from survey',
    icon: Image,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: SubjectPhotos,
    // module size config
    layout: {
      w: 6,
      h: 20,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // default title to display on the module heading
    title: 'Subject Photos',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: features => (features || {}).surveyPhotos || false,
    dependentAccountFeatures: ['surveys'],
  },
  [ModuleType.DICOMViewer]: {
    moduleTypeTitle: 'DICOM Studies',
    moduleTypeDescription: "View a subject's DICOM reports",
    icon: Image,
    hasDynamicHeight: true,
    hasDynamicWidth: true,
    component: DICOMViewer,
    layout: {
      w: 12,
      h: 30,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    allowsSettingsEdit: false,
    title: 'DICOM Studies',
    defaultProps: {},
    wrapInErrorBoundary: true,
    intlErrorMessage,
    isCollapsible: true,
    featureToggled: features => (features || {}).ohifDICOMModule || false,
  },
  [ModuleType.Medications]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Medications',
    moduleTypeDescription: 'View Subject Medications',
    icon: CapsulePills,
    // Does this module dynamically set its
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: Medications,
    // module size config
    layout: {
      w: 6,
      h: 20,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // default title to display on the module heading
    title: 'Medications',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: () => true,
  },
  [ModuleType.Measurements]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Measurements',
    moduleTypeDescription: 'View Subject Measurements',
    icon: Clipboard,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: Measurements,
    // module size config
    layout: {
      w: 6,
      h: 20,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Measurements',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: () => true, // TODO: toggle this on when ready to go-live
  },
  [ModuleType.Wearables]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Wearables',
    moduleTypeDescription: 'View Subject Wearables',
    icon: Watch,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: Wearables,
    // module size config
    layout: {
      w: 6,
      h: 23,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Wearables',
    // the default props to be passed into component
    defaultProps: { metric: 'steps' },
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: () => true,
  },
  [ModuleType.Text]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Text',
    moduleTypeDescription: 'A Rich Text Display',
    icon: Type,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: TextModule,
    // module size config
    layout: {
      w: 6,
      h: 23,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Text',
    // the default props to be passed into component
    defaultProps: {
      textConfig: { text: '' },
    },
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: () => true,
  },
  [ModuleType.Lifeology]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Lifeology',
    moduleTypeDescription: 'Display a Lifeology Course',
    icon: Lifeology,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: LifeologyModule,
    // module size config
    layout: {
      w: 6,
      h: 23,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Lifeology',
    // the default props to be passed into component
    defaultProps: {
      courseUrl: '',
    },
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: () => true,
  },
  [ModuleType.SkillSpring]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'SkillSpring',
    moduleTypeDescription: 'Schedule calls with SkillSpring',
    icon: SkillSpringIcon,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: SkillSpring,
    // module size config
    layout: {
      w: 6,
      h: 5,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'SkillSpring',
    // the default props to be passed into component
    defaultProps: {
      account: '',
    },
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: features =>
      (features || {}).skillSpringSubjectModule || false,
  },
  [ModuleType.SynopticHealth]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Synoptic Health View',
    moduleTypeDescription: 'Subject Synoptic Health View',
    icon: HeartList,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: SynopticHealthView,
    // module size config
    layout: {
      w: 6,
      h: 5,
      minW: 4,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Synoptic Health View',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: features => features?.synopticHealthView,
  },
  [ModuleType.MolecularTumorBoardNotes]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Molecular Tumor Board Notes',
    moduleTypeDescription: 'Molecular Tumor Board Notes',
    icon: Genomics,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: MolecularTumorBoardNotes,
    // module size config
    layout: {
      w: 12,
      h: 20,
      minW: 8,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Molecular Tumor Board Notes',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    dependentAccountProducts: ['MTBNotes'],
  },
  [ModuleType.SurveyResults]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Survey Results',
    moduleTypeDescription: "Subject's survey results and scoring",
    icon: AlignJustify,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: SurveyResults,
    // module size config
    layout: {
      w: 12,
      h: 10,
      minW: 8,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Survey Results',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    dependentAccountFeatures: ['surveys'],
  },
  [ModuleType.SubjectDataUpload]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Subject Data Upload',
    moduleTypeDescription: 'Upload data for subject from an excel workbook',
    icon: Upload,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: WorkbookIngest,
    // module size config
    layout: {
      w: 12,
      h: 24,
      minW: 8,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Subject Data Upload',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
  },
  [ModuleType.TrialMatching]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Subject Trial Matching',
    moduleTypeDescription: 'Find trials subject qualifies for',
    icon: CapsulePills,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: TrialMatching,
    // module size config
    layout: {
      w: 12,
      h: 24,
      minW: 8,
      minH: 8,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Qualifying Clinical Trials',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
  },
  [ModuleType.RiskCalculator]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Risk Calculator',
    moduleTypeDescription: 'Risk Calculator',
    icon: Gauge,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: RiskCalculator,
    // module size config
    layout: {
      w: 3,
      h: 20,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Risk Calculator',
    // the default props to be passed into component
    defaultProps: {
      type: 'cardiovascular',
    },
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: features => features.riskCalculator,
  },
  '35327848-633b-4e20-ae81-d7daf069aca7': {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'CGM Data',
    moduleTypeDescription: 'CGM Data',
    icon: Activity,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: CGMData,
    // module size config
    layout: {
      w: 3,
      h: 20,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'CGM Data',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
  },
  [ModuleType.SubjectFileUpload]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Subject File Upload',
    moduleTypeDescription: 'Upload a file for a subject',
    icon: File,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: SubjectFileUpload,
    // module size config
    layout: {
      w: 4,
      h: 20,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Subject File Upload',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
  },
  [ModuleType.Forms]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Forms',
    moduleTypeDescription: 'Complete Custom Forms Associated with Subject',
    icon: CheckList,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: Forms,
    // module size config
    layout: {
      w: 12,
      h: 40,
      minW: 4,
      minH: 10,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Forms',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
  },
  [ModuleType.Timeline]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Timeline',
    moduleTypeDescription: 'Subject History Timeline',
    icon: ClockRewind,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: StandardTimeline,
    // module size config
    layout: {
      w: 12,
      h: 40,
      minW: 4,
      minH: 10,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Timeline',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: features => features.patientViewerTimelineV2,
  },
  [ModuleType.MLLabeler]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'ML Labeler',
    moduleTypeDescription: 'Label a ML model training example for the Subject',
    icon: Cpu,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: MLLabeler,
    // module size config
    layout: {
      w: 12,
      h: 34,
      minW: 4,
      minH: 10,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'ML Labeler',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    dependentAccountProducts: ['PatientML'],
  },
  [ModuleType.Summary]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Summary',
    moduleTypeDescription: 'Generate subject summary',
    icon: FileText,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    component: Summary,
    // module size config
    layout: {
      w: 12,
      h: 34,
      minW: 4,
      minH: 1,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Subject Summary',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: features => features?.llmAccess,
  },

  [ModuleType.LLMTimeline]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'AI Timeline',
    moduleTypeDescription: 'AI Powered LLM Timeline',
    icon: ClockRewind,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: LLMTimeline,
    // module size config
    layout: {
      w: 12,
      h: 40,
      minW: 4,
      minH: 10,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading

    title: 'AI Timeline',
    // the default props to be passed into component
    defaultProps: {
      llmQuestion: '',
      timeline: {
        timeSpan: {
          value: 1,
          measurement: 'd',
        },
        filters: ['open-filter-search'],
      },
      current: {
        filters: ['open-filter-search'],
      },
    },
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: features =>
      features.patientViewerTimelineV2 && features.llmAccess,
  },
  [ModuleType.ClinicalGenotypes]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Genomics Review',
    moduleTypeDescription: 'Genomics Review',
    icon: Genomics,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: ClinicalGenotypes,
    // module size config
    layout: {
      w: 12,
      h: 20,
      minW: 8,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Genomics Review',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: features => features.clinicalGenotypes,
  },
  [ModuleType.SubjectReports]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Subject Reports',
    moduleTypeDescription: 'Subject Reports',
    icon: FileText,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: SubjectReports,
    // module size config
    layout: {
      w: 12,
      h: 20,
      minW: 0,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Subject Reports',
    // the default props to be passed into component
    defaultProps: {
      codes: [],
    },
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: features => features.subjectReports,
  },
  [ModuleType.Notes]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Notes',
    moduleTypeDescription: 'View Subject Notes',
    icon: Clipboard,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: Notes,
    // module size config
    layout: {
      w: 6,
      h: 20,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Notes',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: () => true,
  },
  [ModuleType.ActionPlan]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Action Plan',
    moduleTypeDescription: "Manage a Member's Action Plan",
    icon: ActivityFolder,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: ActionPlan,
    // module size config
    layout: {
      w: 6,
      h: 30,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    settingsComponent: null,
    // default title to display on the module heading
    title: 'Action Plan',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
  },
  [ModuleType.AnnualReport]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Annual Report',
    moduleTypeDescription: "View a member's Annual Report",
    icon: Sparkle,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: AISystemSummary,
    // module size config
    layout: {
      w: 12,
      h: 30,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    settingsComponent: undefined,
    // default title to display on the module heading
    title: 'Annual Report',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: () => true,
  },
  [ModuleType.LabResults]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'Lab Results',
    moduleTypeDescription: 'Lab Results V2',
    icon: Sparkle,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: LabResults,
    // module size config
    layout: {
      w: 12,
      h: 30,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    settingsComponent: undefined,
    // default title to display on the module heading
    title: 'Lab Results',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
  },
  [ModuleType.AiReports]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'AI Tools',
    moduleTypeDescription: 'AI Tools',
    icon: Sparkle,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: AITools,
    // module size config
    layout: {
      w: 12,
      h: 30,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    settingsComponent: undefined,
    // default title to display on the module heading
    title: 'AI Tools (Beta)',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: (features: Record<string, boolean>) =>
      features?.phcAITools || false,
  },
  [ModuleType.AiChat]: {
    // title, description, and icon for user selecting a module type to add
    moduleTypeTitle: 'AI Chat',
    moduleTypeDescription: 'AI Chat',
    icon: Sparkle,
    // Does this module dynamically set its height
    hasDynamicHeight: false,
    // Does this module dynamically set its width
    hasDynamicWidth: false,
    // The component that will render
    component: AiChat,
    // module size config
    layout: {
      w: 12,
      h: 30,
      minW: 2,
      minH: 0,
      x: 0,
      y: 1000,
      isResizable: true,
    },
    // does this allow for settings to be configured?
    allowsSettingsEdit: false,
    // The component that will render to update settings
    settingsComponent: undefined,
    // default title to display on the module heading
    title: 'Zori',
    // the default props to be passed into component
    defaultProps: {},
    // errors thrown in module content should only prevent render of module itself
    wrapInErrorBoundary: true,
    // error message to display
    intlErrorMessage,
    // can the module be collapsed
    isCollapsible: true,
    featureToggled: (features: Record<string, boolean>) =>
      features?.phcAITools || false,
  },
};
